/**
 * Lightweight event emitter. Avoids reliance on DOM EventTarget which
 * isn't constructable in all browsers targeted by this project.
 */
class EventEmitter {
  constructor() {
    this._listeners = {};
  }

  on(type, fn) {
    (this._listeners[type] || (this._listeners[type] = [])).push(fn);
    return this;
  }

  off(type, fn) {
    const list = this._listeners[type];
    if (list) this._listeners[type] = list.filter(f => f !== fn);
    return this;
  }

  emit(type, detail) {
    (this._listeners[type] || []).forEach(fn => fn(detail));
  }
}

export const MODES = Object.freeze({
  AUTOCOMPLETE: 'autocomplete',
  MAP: 'map',
  GEOCODING: 'geocoding',
});

/**
 * One side of a location search (origin or destination).
 *
 * Holds a primary `location`, an optional `extra` (for port+area combos),
 * and coordinates with a paired slot for cross-filtering and inland detection.
 *
 * Events emitted:
 *   'change' → { field, prev, value, slot }
 */
export class LocationSlot extends EventEmitter {
  constructor(role, initialData = {}) {
    super();
    this._role = role;
    this._mode = MODES.AUTOCOMPLETE;
    this._location = null;
    this._extra = null;
    this._mapPick = null;
    this._paired = null;
    this._initialData = Object.freeze({ ...initialData });
  }

  get role() { return this._role; }
  get mode() { return this._mode; }
  get location() { return this._location; }
  get extra() { return this._extra; }
  get mapPick() { return this._mapPick; }
  get paired() { return this._paired; }
  get isOrigin() { return this._role === 'origin'; }
  get initialData() { return this._initialData; }

  get hasLocation() { return this._location !== null || this._mapPick !== null; }
  get hasExtra() { return this._extra !== null; }
  get isMapPick() { return this._mapPick !== null; }
  get isMultiInput() { return this.hasLocation && this.hasExtra; }

  /**
   * The display country from whichever selection is present,
   * preferring the primary location.
   */
  get country() {
    return this._location?.display_country
      || this._extra?.display_country
      || null;
  }

  // ── Mutators ──────────────────────────────────────────────

  setMode(mode) {
    if (this._mode === mode) return;
    const prev = this._mode;
    this._mode = mode;
    this._emitChange('mode', prev, mode);
  }

  setLocation(item) {
    const prev = this._location;
    this._location = item;
    if (item) this._mapPick = null;
    this._emitChange('location', prev, item);
  }

  setExtra(item) {
    const prev = this._extra;
    this._extra = item;
    if (item) this._mapPick = null;
    this._emitChange('extra', prev, item);
  }

  /**
   * Set selection from map pick: lat, lng, reverse-geocoded address, and nearest area IDs.
   * Clears location/extra. Use for "pick from map" flow.
   */
  setMapPick(data) {
    const prev = this._mapPick;
    this._mapPick = data ? { ...data } : null;
    if (this._mapPick) {
      this._location = null;
      this._extra = null;
    }
    this._emitChange('mapPick', prev, this._mapPick);
  }

  clearMapPick() {
    this.setMapPick(null);
  }

  clearLocation() { this.setLocation(null); }
  clearExtra() { this.setExtra(null); }

  /** Move extra → primary location; clear extra. */
  promoteExtra() {
    const extra = this._extra;
    this._extra = null;
    this._location = extra;
    this._emitChange('location', null, extra);
  }

  /** Move primary location → extra; clear primary. */
  demoteLocation() {
    const loc = this._location;
    this._location = null;
    this._extra = loc;
    this._emitChange('extra', null, loc);
  }

  // ── Pairing ───────────────────────────────────────────────

  pair(other) {
    this._paired = other;
  }

  /**
   * True when both this slot and its paired slot resolve to the same
   * country (i.e. an inland / domestic route).
   */
  isInland() {
    if (!this._paired) return false;
    const mine = this.country;
    const theirs = this._paired.country;
    return mine !== null && theirs !== null && mine === theirs;
  }

  // ── Derived state ─────────────────────────────────────────

  /** Hidden-input `name` for the SEO-friendly location name (e.g. origin_port_name). */
  locationFieldNameForSeo() {
    return `${this.locationFieldName()}_name`;
  }

  /** Hidden-input `name` for the SEO-friendly extra name (e.g. origin_area_name). */
  extraFieldNameForSeo() {
    return `${this.extraFieldName()}_name`;
  }

  /**
   * Hidden-input `name` for the primary location field.
   * Matches the existing `origin_port` / `origin_area` / etc. convention.
   */
  locationFieldName() {
    const prefix = this.isOrigin ? 'origin' : 'destination';
    const suffix = this._location?.port ? 'port' : 'area';
    return `${prefix}_${suffix}`;
  }

  /** Hidden-input `name` for the extra location field. */
  extraFieldName() {
    const prefix = this.isOrigin ? 'origin' : 'destination';
    const isPort = this._extra
      ? this._extra.port
      : (this._location && !this._location.port);
    return `${prefix}_${isPort ? 'port' : 'area'}`;
  }

  /**
   * Whether the currently selected port has matching areas in the dataset
   * (same country, or same continent when the port is in Europe).
   */
  portHasAreas(allLocations) {
    if (!this._location || !this._location.port) return false;
    return allLocations.some(
      loc => !loc.port && this._sameRegion(loc, this._location)
    );
  }

  /** Whether the "add port" affordance should be visible. */
  showAddPort() {
    return this._location
      && !this._location.port
      && !this.hasExtra
      && !this.isInland();
  }

  /** Whether the "add area" affordance should be visible. */
  showAddArea(allLocations) {
    return this._location
      && this._location.port
      && this.portHasAreas(allLocations)
      && !this.isInland();
  }

  // ── Location filtering ────────────────────────────────────

  /**
   * True when loc is in the same region as ref for dropdown filtering.
   * When ref.continent is "europe", match by continent; otherwise match by country.
   */
  _sameRegion(loc, ref) {
    if (!ref) return false;
    if ((ref.continent || '').toLowerCase() === 'europe') {
      return (loc.continent || '').toLowerCase() === 'europe';
    }
    return loc.display_country === ref.display_country;
  }

  /**
   * Given the full locations list and a target field ('location' or 'extra'),
   * return the subset that should appear in the autocomplete dropdown.
   *
   * Rules (ported from the original autocomplete-input.js `filterInputs`):
   *  1. Exclude items matching the opposite slot's selected port/country.
   *  2. When both location and extra are set, lock each to its type+region (country, or continent when Europe).
   *  3. When only one is set, the other field is unrestricted (or locked to ports of the same region if an area is selected as extra).
   */
  filterLocations(allLocations, target) {
    let filtered = allLocations;
    const slotLoc = this._location;
    const slotExtra = this._extra;

    if (this._paired) {
      const opposite = this._paired.location || this._paired.extra;
      if (opposite) {
        filtered = filtered.filter(
          loc => loc.port !== opposite.port
            || loc.display_country !== opposite.display_country
        );
      }
    }

    if (slotLoc && slotExtra) {
      return target === 'location'
        ? filtered.filter(l => !l.port && this._sameRegion(l, slotExtra))
        : filtered.filter(l => l.port && this._sameRegion(l, slotLoc));
    }

    if (slotLoc) {
      return target === 'location'
        ? filtered
        : filtered.filter(l => l.port && this._sameRegion(l, slotLoc));
    }

    if (slotExtra) {
      return target === 'location'
        ? filtered.filter(l => !l.port && this._sameRegion(l, slotExtra))
        : filtered;
    }

    return filtered;
  }

  // ── Internal ──────────────────────────────────────────────

  _emitChange(field, prev, value) {
    this.emit('change', { field, prev, value, slot: this });
  }
}

/**
 * Manages paired origin / destination LocationSlots and the shared
 * locations dataset. Bubbles individual slot `change` events up as
 * store-level `slot:change` events.
 *
 * Events emitted:
 *   'slot:change' → { role, field, prev, value, slot }
 */
export class LocationStore extends EventEmitter {
  constructor(locations = []) {
    super();
    this._slots = new Map();
    this._locations = locations;
  }

  get locations() { return this._locations; }
  set locations(value) { this._locations = value; }

  createSlot(role, initialData = {}) {
    const slot = new LocationSlot(role, initialData);
    this._slots.set(role, slot);

    const opposite = role === 'origin' ? 'destination' : 'origin';
    const other = this._slots.get(opposite);
    if (other) {
      slot.pair(other);
      other.pair(slot);
    }

    slot.on('change', (detail) => {
      this.emit('slot:change', { ...detail, role });
    });

    return slot;
  }

  getSlot(role) {
    return this._slots.get(role);
  }

  get origin() { return this._slots.get('origin'); }
  get destination() { return this._slots.get('destination'); }
}
